import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import api from "@/services/api";
import { toast } from "sonner";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  ArrowLeft,
  Save,
  GraduationCap,
  User,
  Calendar,
  History,
  Info,
  Loader2,
  CheckCircle2,
  AlertCircle,
  TrendingUp,
  TrendingDown,
  Minus,
} from "lucide-react";

export default function CompetencesCollaborateur() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useApp();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [collaborateur, setCollaborateur] = useState(null);
  const [competences, setCompetences] = useState([]);
  const [grille, setGrille] = useState({});
  const [evaluations, setEvaluations] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const [selectedCompetenceHistory, setSelectedCompetenceHistory] = useState(null);

  const canEdit = ["Admin", "RH", "Manager"].includes(currentUser?.role);

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/collaborateurs/${id}/competences`);
      
      setCollaborateur(response.collaborateur);
      setCompetences(response.competences);
      setGrille(response.grille);
      
      // Initialize evaluations state
      const initialEvals = {};
      response.competences.forEach((comp) => {
        initialEvals[comp.id] = {
          niveau: comp.niveau || null,
          commentaire: comp.commentaire || "",
          originalNiveau: comp.niveau || null,
        };
      });
      setEvaluations(initialEvals);
      setHasChanges(false);
    } catch (error) {
      console.error("Error loading data:", error);
      toast.error("Erreur lors du chargement des compétences");
      navigate("/collaborateurs");
    } finally {
      setLoading(false);
    }
  };

  const handleNiveauChange = (competenceId, niveau) => {
    setEvaluations((prev) => ({
      ...prev,
      [competenceId]: {
        ...prev[competenceId],
        niveau: prev[competenceId]?.niveau === niveau ? null : niveau,
      },
    }));
    setHasChanges(true);
  };

  const handleCommentaireChange = (competenceId, commentaire) => {
    setEvaluations((prev) => ({
      ...prev,
      [competenceId]: {
        ...prev[competenceId],
        commentaire,
      },
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      // Prepare evaluations to send
      const evalList = Object.entries(evaluations)
        .filter(([_, eval_]) => eval_.niveau !== null)
        .map(([competenceId, eval_]) => ({
          competence_id: competenceId,
          niveau: eval_.niveau,
          commentaire: eval_.commentaire || null,
        }));
      
      if (evalList.length === 0) {
        toast.warning("Aucune évaluation à enregistrer");
        return;
      }
      
      await api.put(`/api/collaborateurs/${id}/competences`, {
        evaluations: evalList,
      });
      
      toast.success("Évaluations enregistrées avec succès");
      setHasChanges(false);
      loadData(); // Reload to get updated data
    } catch (error) {
      console.error("Error saving evaluations:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const showHistory = (competence) => {
    setSelectedCompetenceHistory(competence);
    setShowHistoryDialog(true);
  };

  // Group competences by category
  const groupedCompetences = useMemo(() => {
    const groups = {};
    competences.forEach((comp) => {
      const cat = comp.categorie || "Autres";
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(comp);
    });
    return groups;
  }, [competences]);

  // Calculate stats
  const stats = useMemo(() => {
    const evaluated = competences.filter((c) => evaluations[c.id]?.niveau);
    const total = competences.length;
    const avgNiveau = evaluated.length > 0
      ? evaluated.reduce((sum, c) => sum + evaluations[c.id].niveau, 0) / evaluated.length
      : 0;
    
    return {
      total,
      evaluated: evaluated.length,
      percentage: total > 0 ? Math.round((evaluated.length / total) * 100) : 0,
      avgNiveau: avgNiveau.toFixed(1),
    };
  }, [competences, evaluations]);

  const getTrend = (competence) => {
    const current = evaluations[competence.id]?.niveau;
    const original = evaluations[competence.id]?.originalNiveau;
    
    if (!current || !original || current === original) return null;
    return current > original ? "up" : "down";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="competences-collaborateur-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <GraduationCap className="h-7 w-7 text-primary" />
              Compétences
            </h1>
            <p className="text-gray-600 flex items-center gap-2 mt-1">
              <User className="h-4 w-4" />
              {collaborateur?.prenom} {collaborateur?.nom}
            </p>
          </div>
        </div>
        {canEdit && hasChanges && (
          <Button onClick={handleSave} disabled={saving} data-testid="save-evaluations-btn">
            {saving ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Enregistrer les modifications
          </Button>
        )}
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-gray-500">Total Compétences</p>
              <p className="text-2xl font-bold">{stats.total}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-gray-500">Évaluées</p>
              <p className="text-2xl font-bold">{stats.evaluated}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-gray-500">Taux d'évaluation</p>
              <p className="text-2xl font-bold">{stats.percentage}%</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-gray-500">Niveau Moyen</p>
              <p className="text-2xl font-bold">
                {stats.evaluated > 0 ? `${stats.avgNiveau}/4` : "-"}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Grille de référence */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Info className="h-4 w-4" />
            Grille de notation
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4 justify-center">
            {Object.entries(grille).map(([niveau, info]) => (
              <TooltipProvider key={niveau}>
                <Tooltip>
                  <TooltipTrigger>
                    <div className="flex items-center gap-2 px-3 py-1 rounded-full" style={{ backgroundColor: info.couleur + "20" }}>
                      <div
                        className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold"
                        style={{ backgroundColor: info.couleur }}
                      >
                        {niveau}
                      </div>
                      <span className="text-sm font-medium" style={{ color: info.couleur }}>
                        {info.label}
                      </span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{info.description}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Competences by Category */}
      <Accordion type="multiple" defaultValue={Object.keys(groupedCompetences)} className="space-y-4">
        {Object.entries(groupedCompetences).map(([categorie, comps]) => (
          <AccordionItem key={categorie} value={categorie} className="border rounded-lg overflow-hidden">
            <AccordionTrigger className="px-4 py-3 bg-gray-50 hover:bg-gray-100">
              <div className="flex items-center gap-3">
                <Badge variant="outline">{categorie}</Badge>
                <span className="text-sm text-gray-500">
                  {comps.filter((c) => evaluations[c.id]?.niveau).length}/{comps.length} évaluées
                </span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="p-0">
              <div className="divide-y">
                {comps.map((competence) => {
                  const eval_ = evaluations[competence.id] || {};
                  const trend = getTrend(competence);
                  
                  return (
                    <div
                      key={competence.id}
                      className="p-4 hover:bg-gray-50 transition-colors"
                      data-testid={`competence-item-${competence.id}`}
                    >
                      <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                        {/* Competence Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h4 className="font-medium text-gray-900">{competence.nom}</h4>
                            {trend === "up" && (
                              <TrendingUp className="h-4 w-4 text-green-500" />
                            )}
                            {trend === "down" && (
                              <TrendingDown className="h-4 w-4 text-red-500" />
                            )}
                          </div>
                          {competence.description && (
                            <p className="text-sm text-gray-500 mt-1">{competence.description}</p>
                          )}
                          {competence.date_evaluation && (
                            <p className="text-xs text-gray-400 mt-1 flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              Dernière évaluation: {new Date(competence.date_evaluation).toLocaleDateString("fr-FR")}
                            </p>
                          )}
                        </div>

                        {/* Level Selection */}
                        <div className="flex items-center gap-2">
                          {Object.entries(grille).map(([niveau, info]) => {
                            const niveauNum = parseInt(niveau);
                            const isSelected = eval_.niveau === niveauNum;
                            
                            return (
                              <TooltipProvider key={niveau}>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <button
                                      onClick={() => canEdit && handleNiveauChange(competence.id, niveauNum)}
                                      disabled={!canEdit}
                                      className={`
                                        w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold
                                        transition-all duration-200 
                                        ${isSelected
                                          ? "ring-2 ring-offset-2 text-white scale-110"
                                          : "bg-gray-100 text-gray-400 hover:bg-gray-200"
                                        }
                                        ${!canEdit && "cursor-not-allowed opacity-60"}
                                      `}
                                      style={isSelected ? { backgroundColor: info.couleur, ringColor: info.couleur } : {}}
                                      data-testid={`niveau-${niveau}-${competence.id}`}
                                    >
                                      {niveau}
                                    </button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p className="font-semibold">{info.label}</p>
                                    <p className="text-xs">{info.description}</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            );
                          })}
                        </div>

                        {/* History Button */}
                        {competence.historique?.length > 0 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => showHistory(competence)}
                            className="text-gray-500"
                          >
                            <History className="h-4 w-4 mr-1" />
                            Historique
                          </Button>
                        )}
                      </div>

                      {/* Comment */}
                      {canEdit && eval_.niveau && (
                        <div className="mt-3 pl-0 lg:pl-4">
                          <Textarea
                            placeholder="Ajouter un commentaire (optionnel)..."
                            value={eval_.commentaire || ""}
                            onChange={(e) => handleCommentaireChange(competence.id, e.target.value)}
                            rows={2}
                            className="resize-none"
                            data-testid={`commentaire-${competence.id}`}
                          />
                        </div>
                      )}
                      {!canEdit && eval_.commentaire && (
                        <div className="mt-3 pl-0 lg:pl-4 p-3 bg-gray-50 rounded-lg">
                          <p className="text-sm text-gray-600">{eval_.commentaire}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>

      {/* Floating Save Button on Mobile */}
      {canEdit && hasChanges && (
        <div className="fixed bottom-4 right-4 sm:hidden">
          <Button
            size="lg"
            onClick={handleSave}
            disabled={saving}
            className="rounded-full shadow-lg"
          >
            {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : <Save className="h-5 w-5" />}
          </Button>
        </div>
      )}

      {/* History Dialog */}
      <Dialog open={showHistoryDialog} onOpenChange={setShowHistoryDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5" />
              Historique - {selectedCompetenceHistory?.nom}
            </DialogTitle>
            <DialogDescription>
              Évolution des évaluations pour cette compétence
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto">
            {selectedCompetenceHistory?.historique?.length > 0 ? (
              selectedCompetenceHistory.historique
                .slice()
                .reverse()
                .map((hist, index) => (
                  <div key={index} className="p-3 border rounded-lg">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div
                          className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold"
                          style={{ backgroundColor: grille[hist.niveau]?.couleur }}
                        >
                          {hist.niveau}
                        </div>
                        <span className="font-medium">{grille[hist.niveau]?.label}</span>
                      </div>
                      {hist.date && (
                        <span className="text-xs text-gray-400">
                          {new Date(hist.date).toLocaleDateString("fr-FR")}
                        </span>
                      )}
                    </div>
                    {hist.commentaire && (
                      <p className="text-sm text-gray-600 mt-2">{hist.commentaire}</p>
                    )}
                  </div>
                ))
            ) : (
              <p className="text-center text-gray-500 py-4">Aucun historique disponible</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowHistoryDialog(false)}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
